import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Sparkles, Coins, Gift, Crown, Share2 } from 'lucide-react';

/**
 * Premium reward celebration — black + gold luxury micro-interaction.
 *
 * A single mount point (place it once near the page root). Trigger from
 * anywhere via:
 *
 *   window.dispatchEvent(new CustomEvent('nofee:celebrate', {
 *     detail: { kind: 'points', title: '+20 Points Earned', subtitle: 'Shared on WhatsApp' }
 *   }))
 *
 * or the helper `celebrate({ kind, title, subtitle })` exported below.
 *
 * Style notes:
 *  - small bottom-center floating toast
 *  - 8 gold particles radiating outward, soft fade
 *  - 2.4s total visible time (~500-700ms fade in/out)
 *  - one-at-a-time queue — never overlaps, never feels loud
 *  - respects prefers-reduced-motion (particles disabled, still fades)
 */

const KIND_META = {
  points:   { Icon: Coins,    aria: 'Points earned' },
  share:    { Icon: Share2,   aria: 'Referral shared' },
  credit:   { Icon: Gift,     aria: 'Gift credits unlocked' },
  tier:     { Icon: Crown,    aria: 'New ambassador tier' },
  generic:  { Icon: Sparkles, aria: 'Reward unlocked' },
};

// 8 particles distributed in a soft 360° fan
const PARTICLES = Array.from({ length: 8 }, (_, i) => {
  const angle = (Math.PI * 2 * i) / 8 + Math.PI / 12;
  const dist = 44 + (i % 2) * 10;
  return {
    tx: Math.cos(angle) * dist,
    ty: Math.sin(angle) * dist - 8, // bias slightly upward
    delay: (i % 4) * 40,
    size: i % 3 === 0 ? 5 : 3.5,
  };
});

export const celebrate = (payload) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('nofee:celebrate', { detail: payload || {} }));
};

const RewardCelebration = () => {
  const [active, setActive] = useState(null); // { id, kind, title, subtitle, phase }
  const queueRef = useRef([]);
  const showingRef = useRef(false);
  const reduced =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const drain = useCallback(() => {
    if (showingRef.current) return;
    const next = queueRef.current.shift();
    if (!next) return;
    showingRef.current = true;
    setActive({ ...next, phase: 'enter' });
    setTimeout(
      () => setActive((a) => (a ? { ...a, phase: 'visible' } : a)),
      30,
    );
    setTimeout(
      () => setActive((a) => (a ? { ...a, phase: 'exit' } : a)),
      1900,
    );
    setTimeout(() => {
      setActive(null);
      showingRef.current = false;
      // drain next item if any
      drain();
    }, 2500);
  }, []);

  // Listen for celebrate events
  useEffect(() => {
    const onCelebrate = (e) => {
      const payload = e.detail || {};
      queueRef.current.push({
        id: `c_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        kind: payload.kind || 'generic',
        title: payload.title || 'Reward unlocked',
        subtitle: payload.subtitle || '',
      });
      drain();
    };
    window.addEventListener('nofee:celebrate', onCelebrate);
    return () => window.removeEventListener('nofee:celebrate', onCelebrate);
  }, [drain]);

  const close = useCallback(() => {
    setActive((a) => (a ? { ...a, phase: 'exit' } : a));
    setTimeout(() => {
      setActive(null);
      showingRef.current = false;
      drain();
    }, 280);
  }, [drain]);

  if (!active) return null;

  const meta = KIND_META[active.kind] || KIND_META.generic;
  const Icon = meta.Icon;
  const visible = active.phase === 'visible' || active.phase === 'enter';
  const isVisiblePeak = active.phase === 'visible';

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={meta.aria}
      data-testid="reward-celebration"
      data-celebration-kind={active.kind}
      className={`fixed z-[60] left-1/2 -translate-x-1/2 bottom-8 sm:bottom-10 pointer-events-none select-none transition-all ${
        visible
          ? 'opacity-100 translate-y-0 duration-[600ms] ease-out'
          : 'opacity-0 translate-y-3 duration-[400ms] ease-in'
      }`}
    >
      <div className="relative">
        {/* Gold particles */}
        {!reduced && isVisiblePeak && (
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            {PARTICLES.map((p, i) => (
              <span
                key={i}
                data-testid={`celebration-particle-${i}`}
                className="absolute left-1/2 top-1/2 rounded-full bg-gradient-to-b from-amber-200 to-gold-500 shadow-[0_0_10px_rgba(212,175,55,0.55)] nofee-particle"
                style={{
                  width: `${p.size}px`,
                  height: `${p.size}px`,
                  // CSS vars consumed by the keyframes
                  ['--tx']: `${p.tx}px`,
                  ['--ty']: `${p.ty}px`,
                  animationDelay: `${p.delay}ms`,
                }}
              />
            ))}
          </div>
        )}

        {/* Soft gold halo */}
        <div
          aria-hidden="true"
          className="absolute inset-0 -m-3 rounded-[28px] bg-gold-500/15 blur-2xl pointer-events-none"
        />

        {/* Toast pill */}
        <div
          onClick={close}
          className="relative pointer-events-auto cursor-pointer flex items-center gap-3 pl-3 pr-5 py-3 rounded-2xl bg-gradient-to-b from-zinc-900 to-black border border-gold-500/45 shadow-[0_20px_50px_-20px_rgba(212,175,55,0.5)] backdrop-blur-md"
        >
          {/* Gold icon disc */}
          <span
            aria-hidden="true"
            className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-b from-amber-300 to-gold-500 flex items-center justify-center shadow-[inset_0_-2px_4px_rgba(0,0,0,0.25)]"
          >
            <Icon className="w-5 h-5 text-black" strokeWidth={2.25} />
          </span>

          <div className="flex flex-col items-start leading-tight pr-1">
            <span
              className="text-[10px] sm:text-[10.5px] uppercase tracking-[0.18em] text-gold-300 font-bold"
              data-testid="celebration-eyebrow"
            >
              Reward
            </span>
            <span
              className="text-[14px] sm:text-[15px] font-bold text-white whitespace-nowrap"
              data-testid="celebration-title"
            >
              {active.title}
            </span>
            {active.subtitle ? (
              <span
                className="text-[11.5px] text-white/55 mt-0.5"
                data-testid="celebration-subtitle"
              >
                {active.subtitle}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RewardCelebration;
